<template>
  <div class="c-conetntsCategory">
    <div class="c-conetntsCategory_select">
      <CSelect
        class="c-conetntsCategory_parent"
        placeholder="大カテゴリを選択してください"
        v-model="parentCategoryId"
        @change.native="selectParentCategory()"
      >
        <option
          v-for="(category, index) in parentCategories"
          :key="index"
          :value="category.id"
        >{{ category.name }}</option>
      </CSelect>
      <CSelect
        class="c-conetntsCategory_child"
        placeholder="小カテゴリを選択してください"
        :select-disabled="!isSelectedParentCategory"
        v-model="childCategoryId"
        v-if="existSubCategories"
        @change.native="selectChildCategory()"
      >
        <option
          v-for="(category, index) in subCategories"
          :key="index"
          :value="category.id"
        >{{ category.name }}</option>
      </CSelect>
      <div
        class="c-conetntsCategory_priceCaution"
        v-if="selectedCategory"
      >
        ※選択カテゴリの最低依頼価格は{{ $util.prettyNumberForRequest(getCategoryLowestPrice(selectedCategory)) }}円です
      </div>
    </div>
    <div
      class="c-conetntsCategory_info"
      v-if="!selectedCategoryId"
    >
      <CoconalaIcon name="info-circle" />
      <span>依頼したいカテゴリを選ぶと、入力項目が表示されます</span>
    </div>
    <BLoading :active="isLoading" />
  </div>
</template>
<script>
import { mapState, mapGetters } from 'vuex'
import CategoryLowestPriceMixin from '../mixins/CategoryLowestPriceMixin'
import { findIndex } from 'lodash-es'

export default {
  name: 'ConetntsCategory',
  mixins: [CategoryLowestPriceMixin],
  data() {
    return {
      parentCategoryId: null,
      childCategoryId: null,
      isLoading: false
    }
  },
  computed: {
    ...mapState('master', ['masterCategories']),
    ...mapGetters('master', ['businessMasterCategories']),
    ...mapState('auth/user', ['isBusiness']),
    parentCategories() {
      if (this.isBusiness) {
        return this.businessMasterCategories
      }
      return this.masterCategories
    },
    isSelectedParentCategory() {
      return !!this.parentCategoryId
    },
    isSelectedChildCategory() {
      return !!this.childCategoryId
    },
    selectedCategoryId() {
      return this.existSubCategories ? this.childCategoryId : this.parentCategoryId
    },
    selectedCategory() {
      if (!this.existSubCategories) {
        return this.parentCategories.find(c => c.id === this.parentCategoryId)
      }
      return this.childCategory
    },
    existSubCategories() {
      // 大カテゴリが未選択の場合は、小カテゴリの選択肢を表示する（disabled）
      if (!this.parentCategoryId) {
        return true
      }

      // 選択した大カテゴリに小カテゴリがない場合は、小カテゴリの選択肢を表示しない
      if (this.subCategories.length === 0) {
        return false
      }

      return true
    },
    subCategories() {
      if (!this.parentCategoryId) {
        return null
      }

      const categoryIndex = findIndex(this.parentCategories, ['id', this.parentCategoryId])
      return this.parentCategories[categoryIndex].subCategories
    },
    childCategory() {
      if (!this.subCategories || !this.childCategoryId) {
        return null
      }
      return this.subCategories.find(sc => sc.id === this.childCategoryId) || null
    }
  },
  methods: {
    selectParentCategory() {
      this.childCategoryId = null

      if (!this.existSubCategories) {
        // その他カテゴリ
        this.selectChildCategory()
        return
      }

      this.$emit('change', 0)
    },
    selectChildCategory() {
      this.$emit('change', this.selectedCategoryId)
      if (this.selectedCategoryId) {
        this.getRequestEditMaster()
      }
    },
    async getRequestEditMaster() {
      this.isLoading = true
      await this.$store
        .dispatch('master/getRequestEditMaster', {
          params: { masterCategoryId: this.selectedCategoryId }
        })
        .catch(err => {
          this.$notify({
            group: 'flash',
            title: '情報の取得に失敗しました。再度お試しください。',
            type: 'error'
          })
          this.parentCategoryId = null
          this.childCategoryId = null
          this.$emit('change', 0)
        })
      this.isLoading = false
    }
  }
}
</script>
<style lang="scss" scoped>
@media (max-width: breakpoint(Requests, M)) {
  .c-conetntsCategory {
    border-top: 1px solid $color-gray-200;

    &_select {
      padding: 16px 12px;
    }

    &_child {
      margin-top: 16px;
    }

    &_priceCaution {
      font-size: 12px;
      line-height: 16px;
      margin-top: 6px;
    }

    &_info {
      display: flex;
      padding: 40px 28px;
      border-top: 1px solid $color-gray-200;
      background-color: $color-gray-50;
      color: $color-gray-700;
      font-size: 12px;

      align-items: center;
      justify-content: center;

      i {
        margin-right: 8px;
        font-size: 16px;
      }

      span {
        display: inline-block;
        min-height: 16px;
        line-height: 1;
      }
    }

    /deep/ .select {
      width: 100%;
      height: 40px;

      select {
        padding-left: 12px;
        width: 100%;
        height: 40px;
        font-size: 14px;
      }
    }
  }
}

@media (min-width: breakpoint(Requests, M) + 1) {
  .c-conetntsCategory {
    &_parent {
      margin-top: 32px;
    }

    &_child {
      margin-top: 32px;
    }

    &_priceCaution {
      font-size: 12px;
      line-height: 16px;
      margin-top: 6px;
    }

    &_info {
      display: flex;
      padding: 108px 0 36px;
      color: $color-gray-700;
      font-size: 16px;

      align-items: center;
      justify-content: center;

      i {
        margin-right: 4px;
        font-size: 20px;
      }
    }

    /deep/ .select {
      width: 100%;
      height: 48px;

      select {
        padding-left: 12px;
        width: 100%;
        height: 48px;
        font-size: 16px;
      }
    }
  }
}
</style>
